import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { FileText, Link as LinkIcon, FolderPlus } from 'lucide-react';
interface DocumentManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (document: any) => void;
  folders: string[];
  onAddFolder: (folder: string) => void;
}
export function DocumentManagerModal({
  isOpen,
  onClose,
  onSave,
  folders,
  onAddFolder
}: DocumentManagerModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [folder, setFolder] = useState(folders[0] || '');
  const [documentType, setDocumentType] = useState<'file' | 'link'>('file');
  const [url, setUrl] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const handleAddFolder = () => {
    if (newFolderName.trim() && !folders.includes(newFolderName.trim())) {
      onAddFolder(newFolderName.trim());
      setFolder(newFolderName.trim());
      setNewFolderName('');
      setShowNewFolder(false);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      id: Math.random().toString(36).substr(2, 9),
      title,
      description,
      folder,
      type: documentType,
      url: url || '#',
      dateAdded: new Date().toISOString()
    });
    setTitle('');
    setDescription('');
    setUrl('');
    setDocumentType('file');
    onClose();
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Add New Document" data-id="element-492">
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-493">
        <div data-id="element-494">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-495">
            Document Title
          </label>
          <input type="text" required value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" placeholder="e.g., Conv/FHA CC Auth Form" data-id="element-496" />
        </div>
        <div data-id="element-497">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-498">
            Description (Optional)
          </label>
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" placeholder="Brief description of the document" data-id="element-499" />
        </div>
        <div data-id="element-500">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-501">
            Document Type
          </label>
          <div className="flex gap-2" data-id="element-502">
            <button type="button" onClick={() => setDocumentType('file')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md border transition-colors ${documentType === 'file' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`} data-id="element-503">
              <FileText className="w-4 h-4" data-id="element-504" />
              Document/PDF
            </button>
            <button type="button" onClick={() => setDocumentType('link')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md border transition-colors ${documentType === 'link' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`} data-id="element-505">
              <LinkIcon className="w-4 h-4" data-id="element-506" />
              External Link
            </button>
          </div>
        </div>
        <div data-id="element-507">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-508">
            {documentType === 'file' ? 'Document URL' : 'External URL'}
          </label>
          <input type="url" value={url} onChange={e => setUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" placeholder={documentType === 'file' ? 'https://example.com/document.pdf' : 'https://lender-portal.com'} data-id="element-509" />
          {documentType === 'file' && <p className="mt-1 text-xs text-gray-500" data-id="element-510">
              Enter the URL where the document is hosted, or upload to your file
              storage first.
            </p>}
        </div>
        <div data-id="element-511">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-512">
            Folder
          </label>
          {!showNewFolder ? <div className="flex gap-2" data-id="element-513">
              <select value={folder} onChange={e => setFolder(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" data-id="element-514">
                {folders.map(f => <option key={f} value={f} data-id="element-515">
                    {f}
                  </option>)}
              </select>
              <button type="button" onClick={() => setShowNewFolder(true)} className="px-3 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 flex items-center gap-1" data-id="element-516">
                <FolderPlus className="w-4 h-4" data-id="element-517" />
              </button>
            </div> : <div className="flex gap-2" data-id="element-518">
              <input type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" placeholder="New folder name" autoFocus data-id="element-519" />
              <button type="button" onClick={handleAddFolder} className="px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700" data-id="element-520">
                Add
              </button>
              <button type="button" onClick={() => setShowNewFolder(false)} className="px-3 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50" data-id="element-521">
                Cancel
              </button>
            </div>}
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-522">
          <Button variant="secondary" onClick={onClose} type="button" data-id="element-523">
            Cancel
          </Button>
          <Button variant="primary" type="submit" data-id="element-524">
            Add Document
          </Button>
        </div>
      </form>
    </Modal>;
}